import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';
import { ArrowRight, Heart } from 'lucide-react';
import { ArrowLeft } from 'lucide-react';
import PageTransition from '../../components/common/PageTransition';
import SEOHead from '../../components/common/SEOHead';
import SectionHeader from '../../components/common/SectionHeader';
import GalleryGrid from '../../components/gallery/GalleryGrid';
import GalleryFilter from '../../components/gallery/GalleryFilter';
import Lightbox from '../../components/gallery/Lightbox';
import { artworks } from '../../data/artworks';
import { ArtCategory, Artwork } from '../../types';

const Gallery: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const [filter, setFilter] = useState<ArtCategory | 'all'>('all');
  const [selectedArtwork, setSelectedArtwork] = useState<Artwork | null>(null);

  useEffect(() => {
    if (location.state?.selectedArtworkId) {
      const art = artworks.find(a => a.id === location.state.selectedArtworkId);
      if (art) {
        setSelectedArtwork(art);
      }
    }
  }, [location.state]);

  const filteredArtworks = filter === 'all'
    ? artworks
    : artworks.filter(a => a.category === filter);

  // Lightbox navigation
  const currentIndex = selectedArtwork ? filteredArtworks.findIndex(a => a.id === selectedArtwork.id) : -1;
  const handleNext = () => {
    if (currentIndex < filteredArtworks.length - 1) {
      setSelectedArtwork(filteredArtworks[currentIndex + 1]);
    }
  };
  const handlePrev = () => {
    if (currentIndex > 0) {
      setSelectedArtwork(filteredArtworks[currentIndex - 1]);
    }
  };

  return (
    <PageTransition>
      <SEOHead title={t('art.gallery.title')} />
      <div className="space-y-8">
        {/* Back navigation */}
        <Link
          to="/art#gallery"
          className="inline-flex items-center gap-2 text-sm font-semibold text-slate-500 hover:text-brand-amber transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('art.projects.backToArt')}
        </Link>

        <SectionHeader
          title={t('art.gallery.title')}
          iconName="ImageIcon"
          variant="art"
          badge={t('nav.art')}
        />

        <GalleryFilter currentFilter={filter} onFilterChange={setFilter} />

        <GalleryGrid
          artworks={filteredArtworks}
          onArtworkClick={setSelectedArtwork}
        />

        {/* Shop Card */}
        <div className="art-card p-6 rounded-2xl border-emerald-500/30 flex flex-col md:flex-row items-center justify-between gap-6 group">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-brand-amber/10 rounded-full text-brand-amber shrink-0 transition-transform group-hover:scale-110">
              <Heart className="w-6 h-6 fill-current" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-brand-amber mb-1">{t('about.shopCard.title')}</h3>
              <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                {t('about.shopCard.desc')}
              </p>
            </div>
          </div>
          <a
            href="https://shop.kynothan.com/"
            target="_blank"
            rel="noopener noreferrer"
            className="whitespace-nowrap inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-brand-amber hover:bg-amber-400 text-slate-900 text-sm font-bold hover:scale-105 transition-transform shadow-md"
          >
            <span>{t('about.shopCard.cta')}</span>
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        <Lightbox
          artwork={selectedArtwork}
          onClose={() => setSelectedArtwork(null)}
          onNext={currentIndex < filteredArtworks.length - 1 ? handleNext : undefined}
          onPrev={currentIndex > 0 ? handlePrev : undefined}
        />
      </div>
    </PageTransition>
  );
};

export default Gallery;
